import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { useDialog } from '@zep/dialog';

import { PlanKeys } from '../../../../consts/spacesConsts';
import {
  useRemovePaymentCard,
  useSubscriptionPlan,
  useUpdateZipCode,
} from '../../../../services';
import { pushGtmPurchaseEvent } from '../../../../utils/gtmMarketingEvent';
import { paymentFailureFromError } from '../../payment-tracking/paymentFailure';
import { createPaymentFailureDialog } from '../../payment-tracking/PaymentFailureContent';
import { useGoSignin } from '../../subscription';
import { findSubscriptionError } from '../../subscription/paymentError';
import {
  usePlanPaymentFormContext,
  usePlanPaymentInfoContext,
} from '../PlanPaymentSection/Providers';

import { usePlanStatusType } from './usePlanStatusType';

export const usePlanSubscriptionAction = () => {
  const { t } = useTranslation(['subscription']);
  const router = useRouter();
  const {
    planPaymentInfo,
    spaceHashId,
    isUpdate,
    selectedPlanInfo,
    setIsPlanPaymentLoading,
  } = usePlanPaymentInfoContext();
  const { watch, getValues } = usePlanPaymentFormContext();
  const { mutateAsync: _mutateSubscriptionPlan } = useSubscriptionPlan();
  const { mutateAsync: removePaymentCard } = useRemovePaymentCard();
  const { mutateAsync: mutateZipCode } = useUpdateZipCode();
  const queryClient = useQueryClient();
  const planType = watch('planType');
  const planStatusType = usePlanStatusType({
    planType,
  });
  const dialog = useDialog();
  const moveSignIn = useGoSignin();

  const handleSubscribePlan = async (_payload: {
    retry: () => void;
    isRemoveBillingKey: boolean;
    /** 결제 버튼에 표시된 실청구액. GTM purchase 신호에 사용된다. */
    chargedAmount?: { value: number; currency: string };
  }) => {
    const { isRemoveBillingKey, chargedAmount } = _payload;

    setIsPlanPaymentLoading(true);
    try {
      const spaceCountryCode = planPaymentInfo?.space.countryCode;
      if (!spaceCountryCode) {
        throw new Error('Missing space country code');
      }

      const { data } = await _mutateSubscriptionPlan({
        spacePlanTemplateId: selectedPlanInfo.id,
        isUpdated: isUpdate,
        spaceHashId,
        countryCode: spaceCountryCode,
      });
      if (spaceCountryCode === 'US') {
        await mutateZipCode({
          spaceHashId,
          zipCode: getValues('zipCode') || '',
        });
      }

      // 마케팅 전환 추적용 purchase 신호 (GTM Custom Event 트리거로 소비).
      // 사용자가 조작 가능한 URL query가 아니라, 서버가 결제를 확인한 응답
      // (payment.orderId)과 결제 버튼의 실청구액(신규=VAT 포함 finalPrice,
      // 업그레이드=추가결제금액)을 사용한다. 즉시 결제가 없는 플로우
      // (다운그레이드 등)는 payment 부재/0원 가드로 제외된다.
      if (data?.payment?.orderId && chargedAmount) {
        pushGtmPurchaseEvent({
          value: chargedAmount.value,
          currency: chargedAmount.currency,
          transactionId: data.payment.orderId,
        });
      }

      moveCompletePage(data?.spacePlan?.spacePlanHashId).catch(console.error);
    } catch (e) {
      setIsPlanPaymentLoading(false);
      const title = t('subscription:payment.alertFailPay.title');
      const { type, message } = findSubscriptionError(e);
      const failure = {
        payment_flow: isUpdate
          ? ('plan_change' as const)
          : ('initial_subscription' as const),
        pg_provider:
          planPaymentInfo?.space.countryCode === 'KR'
            ? ('toss' as const)
            : planPaymentInfo?.space.countryCode
            ? ('payple' as const)
            : ('unknown' as const),
        space_id: planPaymentInfo?.spaceId,
        ...paymentFailureFromError(e),
      };
      const failureDialog = createPaymentFailureDialog(dialog, failure, e);

      switch (type) {
        case '401': {
          await failureDialog.alert({
            title,

            content: t('subscription:payment.alertFailPay.descriptionLogOut'),
            useOutSideClick: false,
          });
          moveSignIn(true).catch(console.error);
          break;
        }
        case 'PG_ERROR': {
          await failureDialog.alert({
            title,
            content: t('subscription:SpaceDetail.changeCardModal.reason', {
              message,
            }),
            useOutSideClick: false,
          });
          break;
        }
        case 'FAIL_TO_EDIT_ZIP_CODE': {
          await dialog.alert({
            title: t('subscription:errorModal.zipCode.title'),
            content: t('subscription:errorModal.zipCode.content'),
          });
          break;
        }
        case 'ZEP_API_ERROR': {
          const confirmed = await failureDialog.confirm({
            title: t('subscription:payment.alertFailPay.title'),
            content: t(
              'subscription:payment.alertFailPay.descriptionErrorCode',
              { errorCode: message },
            ),
            confirmText: t('subscription:payment.alertFailPay.buttonContact'),
          });
          if (confirmed) {
            window.open(t('subscription:pricing.contact.buttonLink'), '_blank');
          }
          break;
        }
        case 'UNKNOWN_ERROR': {
          await failureDialog.alert({
            title,
            content: 'UNKNOWN_ERROR',
            useOutSideClick: false,
          });
        }
      }

      console.error(e);
      /** 신규등록일 경우에만 카드정보를 삭제후 새롭게 요청한다 */
      if (!isUpdate && isRemoveBillingKey) {
        // 서버에서 카드가 지워졌으니 화면의 카드 정보도 다시 받아 등록/변경 판단이 실제 상태를 따르게 한다.
        removePaymentCard(spaceHashId)
          .then(() =>
            queryClient.invalidateQueries(PlanKeys.spaceDetail(spaceHashId)),
          )
          .catch(console.error);
      }

      throw new Error('Error Occurred during subscription plan');
    }
  };

  const moveCompletePage = (spacePlanHashId?: string) => {
    return router.push(
      {
        pathname: `/plan-payment/${spacePlanHashId}/success`,
        query: {
          ...router.query,
          updateStatus: planStatusType,
        },
      },
      `/plan-payment/${spacePlanHashId}/success`,
    );
  };

  return {
    handleSubscribePlan,
  };
};
